import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  AlertTriangle, CheckCircle2, Clock, Download, FilePen, Loader2, ShieldCheck, Upload, XCircle,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import {
  ActivityItem, approveVendorContract, fetchAgreementPdf, fetchSignedCopy, fileExt, getActivity, getVendorContract,
  inr, rejectVendorContract, saveBlob, uploadSignedCopy, VendorContractDetail, VENDOR_TYPE_LABELS,
} from './api';
import StatusBadge from './StatusBadge';
import ActivityTimeline from './ActivityTimeline';

const TABS = ['Overview', 'Agreement', 'Signed copy', 'Activity'] as const;
type Tab = typeof TABS[number];

const fmtDate = (d: string | null | undefined) => (d ? dayjs(d).format('DD MMM YYYY') : '—');
const fmtDateTime = (d: string | null | undefined) => (d ? dayjs(d).format('DD MMM YYYY, HH:mm') : '—');
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

const VendorContractDetailPage: React.FC = () => {
  const { id } = useParams();
  const contractId = Number(id);
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();

  const [c, setC] = useState<VendorContractDetail | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [tab, setTab] = useState<Tab>('Overview');
  const [loadError, setLoadError] = useState<{ notFound: boolean; message: string } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'approve' | 'reject' | 'upload' | null>(null);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Contracts', href: '/contracts' },
      { label: 'Vendor Contracts', href: '/contracts/vendor' },
      { label: c?.contract_number || 'Contract' },
    ]);
  }, [setBreadcrumbs, c?.contract_number]);

  const load = useCallback(async () => {
    const [d, a] = await Promise.all([getVendorContract(contractId), getActivity(contractId)]);
    setC(d);
    setActivity(a.activity);
  }, [contractId]);

  useEffect(() => {
    load().catch((e: any) => setLoadError({ notFound: e?.status === 404, message: e?.message || 'Could not load the contract' }));
  }, [load]);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 3000); };

  const downloadPdf = async () => {
    if (!c) return;
    setError(null);
    try {
      const blob = await fetchAgreementPdf(c.id, true);
      saveBlob(blob, `${c.contract_number}_${c.vendor_name.replace(/[^A-Za-z0-9]+/g, '-')}.pdf`);
      getActivity(c.id).then((a) => setActivity(a.activity)).catch(() => {});
    } catch (e: any) {
      setError(e?.message || 'Could not download the agreement');
    }
  };

  if (loadError) {
    return (
      <div className="px-4 sm:px-6 pb-6 space-y-3">
        <h1 className="text-2xl font-semibold">{loadError.notFound ? 'Contract not found' : 'Could not load the contract'}</h1>
        <p className="text-sm text-muted-foreground">{loadError.notFound ? 'It may not exist, or it isn’t shared with you.' : loadError.message}</p>
        <Button variant="outline" onClick={() => navigate('/contracts/vendor')}>Back to vendor contracts</Button>
      </div>
    );
  }
  if (!c) return <div className="px-4 sm:px-6 pb-6 text-muted-foreground">Loading…</div>;

  const name = (ecode: string | null) => (ecode ? c.user_names[ecode] || ecode : '—');
  const tabs = TABS.filter((t) => t !== 'Signed copy' || !!c.signed_copy_name);

  return (
    <div className="px-4 sm:px-6 pb-8 space-y-4">
      {/* ---- heading + actions ---- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">{c.vendor_name}</h1>
            <StatusBadge status={c.status} />
          </div>
          <p className="text-sm text-muted-foreground">
            {c.contract_number} · {c.template_title} · {VENDOR_TYPE_LABELS[c.vendor_type] || c.vendor_type} · {c.load_type}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.can_approve && (
            <>
              <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => setDialog('reject')}>
                <XCircle className="mr-1 h-4 w-4" /> Reject
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => setDialog('approve')}>
                <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
              </Button>
            </>
          )}
          {c.can_edit && (
            <Button onClick={() => navigate(`/contracts/vendor/${c.id}/edit`)}><FilePen className="mr-1 h-4 w-4" /> Edit & resubmit</Button>
          )}
          {c.has_pdf && <Button variant="outline" onClick={downloadPdf}><Download className="mr-1 h-4 w-4" /> Download PDF</Button>}
          {c.can_upload_signed && <Button onClick={() => setDialog('upload')}><Upload className="mr-1 h-4 w-4" /> Upload signed copy</Button>}
          <Button variant="outline" onClick={() => navigate('/contracts/vendor')}>Back</Button>
        </div>
      </div>

      {msg && <div className="rounded bg-green-50 px-3 py-2 text-sm text-green-700">{msg}</div>}
      {error && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <StageBanner c={c} name={name} />

      {/* ---- tabs ---- */}
      <div className="flex gap-1 border-b">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t ? 'border-blue-600 font-medium' : 'border-transparent text-muted-foreground'}`}
          >
            {t === 'Agreement' && !c.has_pdf ? 'Agreement (draft)' : t}
          </button>
        ))}
      </div>

      {tab === 'Overview' && <Overview c={c} name={name} />}
      {tab === 'Agreement' && <AgreementViewer c={c} onDownload={downloadPdf} />}
      {tab === 'Signed copy' && c.signed_copy_name && (
        <SignedCopyViewer c={c} onDownloaded={() => getActivity(c.id).then((a) => setActivity(a.activity)).catch(() => {})} />
      )}
      {tab === 'Activity' && <ActivityTimeline items={activity} />}

      {/* ---- dialogs ---- */}
      <ApproveDialog open={dialog === 'approve'} c={c} onClose={() => setDialog(null)}
        onDone={async () => { setDialog(null); await load(); flash('Contract approved — the agreement PDF is ready to download'); }} />
      <RejectDialog open={dialog === 'reject'} c={c} onClose={() => setDialog(null)}
        onDone={async () => { setDialog(null); await load(); flash('Contract rejected — the submitter has been notified'); }} />
      <UploadDialog open={dialog === 'upload'} c={c} onClose={() => setDialog(null)}
        onDone={async () => { setDialog(null); await load(); setTab('Signed copy'); flash('Signed copy uploaded — contract executed'); }} />
    </div>
  );
};

// ---- where the contract is in its lifecycle, and what happens next ----
const StageBanner: React.FC<{ c: VendorContractDetail; name: (e: string | null) => string }> = ({ c, name }) => {
  const life = c.lifecycle_status;
  return (
    <div className="space-y-2">
      {life === 'pending_approval' && (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-900">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Submitted by {name(c.created_by)} on {fmtDateTime(c.submitted_at)} — waiting for approval.
            {c.approve_blocked_reason === "You can't approve a contract you submitted" && ' Another approver has to approve it.'}
          </p>
        </div>
      )}
      {life === 'rejected' && (
        <div className="flex gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Rejected by {name(c.rejected_by)} on {fmtDateTime(c.rejected_at)}</p>
            <p>{c.rejection_reason}</p>
            {c.can_edit && <p className="mt-1 text-xs">Edit the contract and resubmit it — it keeps the number {c.contract_number}.</p>}
          </div>
        </div>
      )}
      {life === 'approved' && (
        <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Approved by {name(c.approved_by)} on {fmtDateTime(c.approved_at)} · agreement dated {fmtDate(c.agreement_date)}</p>
            <p>Next: download the PDF, get it signed by MRC and the vendor, then upload the signed copy to execute the contract.</p>
          </div>
        </div>
      )}
      {life === 'executed' && (
        <div className="flex gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Executed — signed copy uploaded by {name(c.executed_by)} on {fmtDateTime(c.executed_at)}.
            {c.status === 'expired' && ` The contract expired on ${fmtDate(c.valid_to)}.`}
          </p>
        </div>
      )}
    </div>
  );
};

// ---- overview: everything that prints, laid out for review ----
const Rows: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
  <dl className="grid grid-cols-[12rem_1fr] text-sm">
    {rows.map(([label, value], i) => (
      <React.Fragment key={label}>
        <dt className={`px-4 py-2.5 text-muted-foreground ${i ? 'border-t' : ''}`}>{label}</dt>
        <dd className={`px-4 py-2.5 ${i ? 'border-t' : ''}`}>{value === null || value === undefined || value === '' ? <span className="text-muted-foreground">—</span> : value}</dd>
      </React.Fragment>
    ))}
  </dl>
);

const Card: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <section className={`overflow-hidden rounded-lg border bg-card ${className || ''}`}>
    <h2 className="border-b bg-muted/40 px-4 py-2.5 text-sm font-semibold">{title}</h2>
    {children}
  </section>
);

const Overview: React.FC<{ c: VendorContractDetail; name: (e: string | null) => string }> = ({ c, name }) => {
  const address = [c.address_line1, c.address_line2, c.city].filter(Boolean).join(', ') + `, ${c.state} – ${c.pincode}`;
  const pan = (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="font-mono">{c.vendor_pan}</span>
      {c.pan_status === 'VALID'
        ? <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"><ShieldCheck className="h-3 w-3" /> Verified</span>
        : <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">{c.pan_status || 'Unverified'}</span>}
      {c.pan_name_match === false && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-help items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                <AlertTriangle className="h-3 w-3" /> Name mismatch
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              The PAN {c.vendor_pan} is registered to <span className="font-medium">{c.pan_registered_name}</span>, but the agreement
              names “{c.vendor_name}”. Check before approving.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
  );
  const payment = c.advance_pct === 0
    ? `100% payment in ${plural(c.balance_days, 'day')} after delivery + clean POD`
    : c.advance_pct === 100 ? '100% advance'
    : `${c.advance_pct}% advance, balance in ${plural(c.balance_days, 'day')} after delivery + clean POD`;

  return (
    <div className="grid max-w-6xl grid-cols-1 gap-4 xl:grid-cols-2">
      <Card title="Vendor">
        <Rows rows={[
          ['Name', c.vendor_name],
          ['PAN', pan],
          ['PAN registered name', c.pan_registered_name],
          ['Vendor type', VENDOR_TYPE_LABELS[c.vendor_type] || c.vendor_type],
          ['Load type', c.load_type],
          ['Address', address],
          ['Signatory', c.signatory_name],
          ['Phone', c.contact_phone],
          ['Email', c.contact_email],
        ]} />
      </Card>

      <Card title="Contract">
        <Rows rows={[
          ['Agreement no.', c.contract_number],
          ['Validity', `${fmtDate(c.valid_from)} – ${fmtDate(c.valid_to)}`],
          ['Agreement date', c.agreement_date ? fmtDate(c.agreement_date) : <span className="text-muted-foreground">Set on approval</span>],
          ['Created by', `${name(c.created_by)} · ${fmtDateTime(c.created_at)}`],
          ['Last submitted', fmtDateTime(c.submitted_at)],
          ['Approved by', c.approved_by ? `${name(c.approved_by)} · ${fmtDateTime(c.approved_at)}` : null],
          ['Executed by', c.executed_by ? `${name(c.executed_by)} · ${fmtDateTime(c.executed_at)}` : null],
        ]} />
      </Card>

      <Card title={`Routes & freight (${c.lanes.length})`} className="xl:col-span-2">
        <Table className="[&_td]:px-4 [&_td]:py-2 [&_th]:h-auto [&_th]:px-4 [&_th]:py-2">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10">#</TableHead>
              <TableHead>Route / lane</TableHead>
              <TableHead>Vehicle type</TableHead>
              <TableHead className="text-right">Lorry hire rate</TableHead>
              <TableHead>Rate basis</TableHead>
              <TableHead className="text-right">Transit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {c.lanes.map((l) => (
              <TableRow key={l.line_no} className="hover:bg-transparent">
                <TableCell className="text-muted-foreground">{l.line_no}</TableCell>
                <TableCell>{l.route}</TableCell>
                <TableCell>{l.vehicle_type}</TableCell>
                <TableCell className="whitespace-nowrap text-right font-medium">{inr(l.rate)}</TableCell>
                <TableCell className="text-muted-foreground">{l.rate_basis}</TableCell>
                <TableCell className="whitespace-nowrap text-right">{plural(l.transit_days, 'day')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card title="Commercial terms" className="xl:col-span-2">
        <Rows rows={[
          ['Security deposit / PG', inr(c.security_deposit)],
          ['Placement timeline', plural(c.placement_hours, 'hour')],
          ['Loading / unloading free time', `${plural(c.free_time_hours, 'hour')} each`],
          ['Detention charges', `${inr(c.detention_per_day)} per day`],
          ['Late delivery penalty', `${inr(c.late_delivery_per_day)} per day`],
          ['POD delay penalty', `${inr(c.pod_delay_per_day)} per day`],
          ['Diesel escalation', `±${c.diesel_threshold_pct}% threshold, FAF ${c.diesel_faf_pct}% · base ${inr(c.diesel_base_price)}/L (${c.diesel_base_city}, ${fmtDate(c.diesel_base_date)})`],
          ['Non-placement penalty', c.non_placement_penalty],
          ['Payment terms', payment],
        ]} />
      </Card>
    </div>
  );
};

// ---- the agreement PDF: the stored one once approved, a DRAFT render before ----
const AgreementViewer: React.FC<{ c: VendorContractDetail; onDownload: () => void }> = ({ c, onDownload }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let u: string | null = null;
    let cancelled = false;
    fetchAgreementPdf(c.id)
      .then((blob) => { if (!cancelled) { u = URL.createObjectURL(blob); setUrl(u); } })
      .catch((e: any) => !cancelled && setErr(e?.message || 'Could not load the agreement'));
    return () => { cancelled = true; if (u) URL.revokeObjectURL(u); };
  }, [c.id, c.has_pdf, c.lifecycle_status]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {c.has_pdf
            ? `The agreement as generated on approval (dated ${fmtDate(c.agreement_date)}).`
            : 'Draft of the agreement with the current details. The date and final PDF are set when it is approved.'}
        </p>
        {c.has_pdf && <Button size="sm" variant="outline" onClick={onDownload}><Download className="mr-1 h-4 w-4" /> Download</Button>}
      </div>
      {err && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {!url && !err && <div className="flex h-40 items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rendering…</div>}
      {url && <iframe title="Agreement" src={url} className="h-[80vh] w-full rounded-lg border" />}
    </div>
  );
};

const SignedCopyViewer: React.FC<{ c: VendorContractDetail; onDownloaded: () => void }> = ({ c, onDownloaded }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const isPdf = fileExt(c.signed_copy_name || '') === 'pdf';
  useEffect(() => {
    let u: string | null = null;
    fetchSignedCopy(c.id, c.signed_copy_name || '')
      .then((blob) => { u = URL.createObjectURL(blob); setUrl(u); })
      .catch((e: any) => setErr(e?.message || 'Could not load the signed copy'));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [c.id, c.signed_copy_name]);

  const download = async () => {
    const blob = await fetchSignedCopy(c.id, c.signed_copy_name || '', true);
    saveBlob(blob, `${c.contract_number}_SIGNED.${fileExt(c.signed_copy_name || 'pdf')}`);
    onDownloaded();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{c.signed_copy_name} · signed by both parties</p>
        <Button size="sm" variant="outline" onClick={download}><Download className="mr-1 h-4 w-4" /> Download</Button>
      </div>
      {err && <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {url && (isPdf
        ? <iframe title="Signed copy" src={url} className="h-[80vh] w-full rounded-lg border" />
        : <img src={url} alt="Signed copy" className="max-w-full rounded-lg border" />)}
    </div>
  );
};

// ---- dialogs ----
const ApproveDialog: React.FC<{ open: boolean; c: VendorContractDetail; onClose: () => void; onDone: () => void }> = ({ open, c, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true); setErr(null);
    try { await approveVendorContract(c.id); onDone(); } catch (e: any) { setErr(e?.message || 'Approval failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve {c.contract_number}?</DialogTitle>
          <DialogDescription>
            The agreement with {c.vendor_name} will be dated today ({dayjs().format('D MMMM YYYY')}) and its PDF generated.
            An approved contract can't be changed — any change needs a new contract.
          </DialogDescription>
        </DialogHeader>
        {c.pan_name_match === false && (
          <p className="flex gap-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> The vendor name doesn't match the PAN's registered name ({c.pan_registered_name}).
          </p>
        )}
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-green-600 hover:bg-green-700" onClick={go} disabled={busy}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Approve & generate PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RejectDialog: React.FC<{ open: boolean; c: VendorContractDetail; onClose: () => void; onDone: () => void }> = ({ open, c, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) { setReason(''); setErr(null); } }, [open]);
  const go = async () => {
    setBusy(true); setErr(null);
    try { await rejectVendorContract(c.id, reason.trim()); onDone(); } catch (e: any) { setErr(e?.message || 'Rejection failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject {c.contract_number}?</DialogTitle>
          <DialogDescription>The submitter will be emailed the reason and can edit and resubmit the contract.</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="mb-1.5 block">Reason <span className="text-red-500">*</span></Label>
          <Textarea rows={4} value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)}
            placeholder="What needs to change, e.g. the detention charges should be ₹1,200 per day" />
        </div>
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={go} disabled={busy || reason.trim().length < 3}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UploadDialog: React.FC<{ open: boolean; c: VendorContractDetail; onClose: () => void; onDone: () => void }> = ({ open, c, onClose, onDone }) => {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (open) { setFile(null); setErr(null); } }, [open]);
  const go = async () => {
    if (!file) return;
    setBusy(true); setErr(null);
    try { await uploadSignedCopy(c.id, file); onDone(); } catch (e: any) { setErr(e?.message || 'Upload failed'); } finally { setBusy(false); }
  };
  const tooBig = !!file && file.size > 25 * 1024 * 1024;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload signed copy</DialogTitle>
          <DialogDescription>
            Upload the agreement signed by both MRC and {c.vendor_name}. This executes the contract and can't be undone.
          </DialogDescription>
        </DialogHeader>
        <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center hover:bg-muted/40">
          <Upload className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm font-medium">{file ? file.name : 'Choose a file'}</span>
          <span className="text-xs text-muted-foreground">PDF, PNG or JPG · up to 25 MB</span>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        {tooBig && <p className="text-sm text-red-600">That file is over 25 MB.</p>}
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={go} disabled={busy || !file || tooBig}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Upload & execute
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VendorContractDetailPage;
